import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Chip,
  Typography,
  TableContainer,
  Paper,
  useTheme,
} from "@mui/material";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import { toast } from "react-toastify";

export default function PartnerCommissions({ partnerId }: { partnerId: string }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [amountMap, setAmountMap] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${ipconfig}/admin/partner/commissions?partnerId=${partnerId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setData(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateCommission = async (id: string, paid: boolean) => {
    try {
      const amt = amountMap[id] !== undefined ? amountMap[id] : data.find(c => c._id === id)?.commissionAmount;
      await axios.put(
        `${ipconfig}/partner/referral/commission?referralId=${id}`,
        {
          isEligible: true,
          commissionAmount: Number(amt),
          isCommissionPaid: paid,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Commission status updated");
      fetchCommissions();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update commission");
    }
  };

  const updateRenewalCommission = async (referralId: string, renewalId: string, paid: boolean) => {
    try {
      await axios.put(
        `${ipconfig}/partner/referral/commission?referralId=${referralId}&renewalId=${renewalId}&type=renewal`,
        {
          isCommissionPaid: paid,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Renewal commission updated");
      fetchCommissions();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update renewal commission");
    }
  };

  useEffect(() => {
    if (partnerId) {
      fetchCommissions();
    }
  }, [partnerId]);

  if (loading) {
    return <Spinner height="200px" />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
        Partner Commissions ({data.length})
      </Typography>

      {data.length > 0 ? (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: "12px",
            maxHeight: "450px",
            "&::-webkit-scrollbar": {
              width: "5px",
              height: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "divider",
              borderRadius: "4px",
            },
          }}
        >
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Customer & Plan</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Type / Event</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Commission Amount (₹)</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b", textAlign: "right" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((c, idx) => {
                const isPaid = c.isCommissionPaid;
                const renewals = c.renewals || [];

                return (
                  <React.Fragment key={c._id || idx}>
                    {/* First Subscription Row */}
                    <TableRow
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b50",
                        },
                        transition: "all 0.15s ease",
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {c.customerName}
                        <Typography 
                          variant="caption" 
                          display="block" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (c.email && c.email !== "—") {
                              navigate("/user", { state: { email: c.email, search: c.email } });
                            }
                          }}
                          sx={{ 
                            color: c.email && c.email !== "—" ? "primary.main" : "text.secondary", 
                            fontWeight: c.email && c.email !== "—" ? 600 : 400,
                            cursor: c.email && c.email !== "—" ? "pointer" : "default",
                            "&:hover": c.email && c.email !== "—" ? { textDecoration: "underline" } : {}
                          }}
                        >
                          {c.plan || "—"} ({c.email})
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={`First Subscription (${c.commissionPercentage || 30}%)`} color="primary" variant="outlined" sx={{ fontWeight: 600, fontSize: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          disabled={isPaid}
                          value={amountMap[c._id] ?? c.commissionAmount ?? 0}
                          onChange={(e) =>
                            setAmountMap({
                              ...amountMap,
                              [c._id]: Number(e.target.value),
                            })
                          }
                          sx={{ width: "120px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            c.isCommissionPaid
                              ? "Disbursed"
                              : c.isEligible
                                ? "Pending Payout"
                                : "Not Eligible"
                          }
                          color={
                            c.isCommissionPaid
                              ? "success"
                              : c.isEligible
                                ? "warning"
                                : "default"
                          }
                          sx={{ fontWeight: 700, fontSize: "11px" }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: "right" }}>
                        {!isPaid && c.isEligible ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => updateCommission(c._id, true)}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            Mark Paid
                          </Button>
                        ) : isPaid ? (
                          <Typography variant="caption" sx={{ color: "success.main", fontWeight: 600 }}>
                            Disbursed
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => updateCommission(c._id, false)}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            Save Amount
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Renewal Rows (if any) */}
                    {renewals.map((ren: any, rIdx: number) => {
                      const renPaid = ren.isCommissionPaid;
                      return (
                        <TableRow
                          key={ren._id || rIdx}
                          sx={{
                            backgroundColor: theme.palette.mode === "light" ? "#f9fafb" : "#111827",
                            "&:hover": {
                              backgroundColor: theme.palette.mode === "light" ? "#f1f5f9" : "#1e293b",
                            },
                          }}
                        >
                          <TableCell sx={{ pl: 4, fontStyle: "italic", fontSize: "12px", color: "text.secondary" }}>
                            ↳ Renewal: {ren.planName || c.plan}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={`Renewal #${rIdx + 1} (${ren.commissionPercentage || 10}%)`} color="secondary" variant="outlined" sx={{ fontWeight: 600, fontSize: "11px" }} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "13px" }}>
                            ₹{ren.commissionAmount}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={renPaid ? "Disbursed" : "Pending Payout"}
                              color={renPaid ? "success" : "warning"}
                              sx={{ fontWeight: 700, fontSize: "11px" }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: "right" }}>
                            {!renPaid ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => updateRenewalCommission(c._id, ren._id, true)}
                                sx={{ textTransform: "none", fontWeight: 600 }}
                              >
                                Mark Paid
                              </Button>
                            ) : (
                              <Typography variant="caption" sx={{ color: "success.main", fontWeight: 600 }}>
                                Disbursed
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 4, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: "12px" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
            No commission details found for this partner.
          </Typography>
        </Box>
      )}
    </Box>
  );
}